
/*  ActiveIncidentPage.jsx – full file */
import React, { useState, useEffect, useCallback, useContext } from 'react';
import {
  Card, Container, Row, Col, Button, Badge, ListGroup, Modal
} from 'react-bootstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ExclamationTriangleFill,
  GeoAltFill,
  GearFill,
  PatchExclamationFill,
  TelephoneFill,
  Power,
  ArrowsAngleExpand,
} from 'react-bootstrap-icons';

import IncidentAlertChat    from './IncidentAlertChat';
import DashboardCardHeader  from '../ViewReports/DashboardCardHeader';
import ReportStatusModal    from './ReportStatusModal';
import IncidentTypeModal    from './IncidentTypeModal';
import UpdateLocationModal  from './UpdateLocationModal';
import LocationEditorModal  from './LocationEditorModal';
import ActionGuideModal     from './ActionGuideModal';
import logo                 from '../ViewReports/SchoolShield_color300x3xx.webp';

import axios, {
  setInactiveIncidentWriteGate,
  clearInactiveIncidentWriteGate,
} from '../axiosAlertConfig';

import { UserContext }      from '../../userAlertContext';
import activeStyles from './ActiveIncidentPage.module.css';
import useOperationalUpdates from '../../hooks/useOperationalUpdates';


/* ---------- helpers --------------------------------------------- */
const srpColour = (txt = '') => {
  const k = txt.toLowerCase();
  if (k.includes('lock'))    return 'danger';
  if (k.includes('evac'))    return 'warning';
  if (k.includes('secure'))  return 'primary';
  if (k.includes('shelter')) return 'info';
  if (k.includes('hold'))    return 'secondary';
  return 'dark';
  
};

const parseStatus = raw => {
  if (!raw)           return [];
  if (Array.isArray(raw)) return raw;
  try   { return JSON.parse(raw); }
  catch { return [String(raw)]; }
};

/* ---------- component ------------------------------------------- */
export default function ActiveIncidentPage() {
  const { state }      = useLocation();
  const { incidentId } = useParams();
  const navigate       = useNavigate();
  const {
    user,
    setUser,
    iBuildingID,
    setIBuildingID,
    setBuildingName,
  } = useContext(UserContext);
  

  /* main incident */
  const [incident, setIncident] = useState(state?.incident || null);

  /* UI state */
  const [showStatusModal,   setShowStatusModal]   = useState(false);
  const [showIncidentModal, setShowIncidentModal] = useState(false);
  const [showHelp,          setShowHelp]          = useState(false);

   const [showActionGuide, setShowActionGuide] = useState(false);
  const [showActionGuideButton, setShowActionGuideButton] = useState(false);
  const [guideLoading, setGuideLoading] = useState(false);
  const [guideText, setGuideText] = useState('');



  const [incidentTypeName,  setIncidentTypeName ] = useState(incident?.incidentType || '');
  const [incidentCategory,  setIncidentCategory]  = useState(incident?.incidentCategory || '');
  const [statusArr,         setStatusArr]         = useState(parseStatus(incident?.myStatus));
  const [creatorStatus,     setCreatorStatus]     = useState(parseStatus(incident?.myStatus));

  // location state
  const [showLocModal,  setShowLocModal]  = useState(false);
  const [showLocEditor, setShowLocEditor] = useState(false);
  const [myLocation,    setMyLocation]    = useState(incident?.myLocation || '');
const [showAdminPost, setShowAdminPost] = useState(false);
// Gate pulses until we've loaded server values at least once
const [statusReady, setStatusReady] = useState(false);
const [locationReady, setLocationReady] = useState(false);

  const isAdmin = ['administrator','systemadministrator']
                    .includes((user?.userRole || '').toLowerCase());

   const isAlertUser = ['alertuser', 'alertusers']
  .includes((user?.userRole || '').toLowerCase());                 

  const AUTO_SUPPRESS_MS = 3000; // keep in sync with panel
  const ADMIN_POST_POLL_MS = 10000;

  // ---- per-user persistence (survives refresh) -------------------
  const incidentKey = String(incidentId || incident?.incidentID || '');
  const userKey = String(user?.userID || '');
  const statusStorageKey = incidentKey && userKey ? `aip:${incidentKey}:${userKey}:statusArr` : '';
  const locStorageKey    = incidentKey && userKey ? `aip:${incidentKey}:${userKey}:myLocation` : '';

  const hasNonEmptyStatus = (arr) =>
    Array.isArray(arr) && arr.some(s => String(s || '').trim());

  const actionGuideAccountID = String(incident?.iAccountID || user?.iAccountID || '').trim();

  // Mark we entered Active; on unmount, give the panel a small TTL to avoid bounce.
  useEffect(() => {
    sessionStorage.setItem('activeOpenAt', String(Date.now()));
    return () => {
      sessionStorage.setItem(
        'suppressAutoOpenUntil',
        String(Date.now() + AUTO_SUPPRESS_MS)
      );
    };
  }, []);

  /* fetch on hard refresh / deep-link */
  useEffect(() => {
    if (incident || !incidentId) return;
    (async () => {
      try {
        const { data } = await axios.get(`/ialert/incidents/${incidentId}`);
        if (data) {
          const obj = Array.isArray(data) ? data[0] : data;
          setIncident(obj);
          setStatusArr(parseStatus(obj.myStatus));
        }
      } catch (e) { console.error('load incident', e); }
    })();
  }, [incident, incidentId]);

  /* The incident record is authoritative for campus selection. */
  useEffect(() => {
    if (!incident?.incidentID) return;

    const incidentBuildingID = String(
      incident.iBuildingID || incident.iCampusID || ''
    ).trim();
    const incidentBuildingName = String(incident.buildingName || '').trim();

    if (incidentBuildingID) {
      setIBuildingID?.(incidentBuildingID);
      localStorage.setItem('iBuildingID', incidentBuildingID);
      sessionStorage.setItem('iBuildingID', incidentBuildingID);
    }

    setBuildingName?.(incidentBuildingName);
    localStorage.setItem('buildingName', incidentBuildingName);
    sessionStorage.setItem('buildingName', incidentBuildingName);
  }, [
    incident?.incidentID,
    incident?.iBuildingID,
    incident?.iCampusID,
    incident?.buildingName,
    setIBuildingID,
    setBuildingName,
  ]);


  /* keep axios write gate synced to this incident */
  useEffect(() => {
    if (!incident?.incidentID) {
      clearInactiveIncidentWriteGate();
      return;
    }

    setInactiveIncidentWriteGate({
      incidentID: incident.incidentID,
      isActive: incident.isActive,
    });

    return () => {
      clearInactiveIncidentWriteGate();
    };
  }, [incident?.incidentID, incident?.isActive]);



  // ---- rehydrate status (per user) after refresh ------------------
  useEffect(() => {
    if (!statusStorageKey) return;

    // If we already have status, don't overwrite it
    if (hasNonEmptyStatus(statusArr)) return;

    try {
      const raw = sessionStorage.getItem(statusStorageKey);
      if (!raw) return;

      const saved = parseStatus(raw);
      if (hasNonEmptyStatus(saved)) setStatusArr(saved);
    } catch {
      // ignore storage errors
    }
  }, [statusStorageKey, incident?.incidentID, statusArr]);

  // ---- rehydrate location (per user) after refresh ----------------
  useEffect(() => {
    if (!locStorageKey) return;
    if (!incident) return;

    const current = String(incident?.myLocation || '').trim();
    if (current) return;

    try {
      const saved = String(sessionStorage.getItem(locStorageKey) || '').trim();
      if (!saved) return;

      setIncident(prev => {
        if (!prev) return prev;
        const prevLoc = String(prev.myLocation || '').trim();
        if (prevLoc) return prev;
        return { ...prev, myLocation: saved };
      });
    } catch {
      // ignore storage errors
    }
  }, [locStorageKey, incident, incident?.myLocation]);




 useEffect(() => {
   // if (incident || !incidentId) return;
     
//console.log('[ACTIVEINCIDENTPAGE] INCIDENT ',incident )


  }, [incident, incidentId]);


  /* ── load Action Guide button visibility (account-level) ───────── */
  useEffect(() => {
    if (!actionGuideAccountID) {
      setShowActionGuideButton(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const { data } = await axios.get('/action-guides/action-guide-button-visibility', {
          params: { iAccountID: actionGuideAccountID },
          withCredentials: true,
        });

        if (!cancelled) {
          setShowActionGuideButton(!!data?.showActionGuideButton);
        }
      } catch (e) {
        console.error('load action guide button visibility', e);
        if (!cancelled) setShowActionGuideButton(false);
      }
    })();

    return () => { cancelled = true; };
  }, [actionGuideAccountID]);

  useEffect(() => {
    if (!showActionGuideButton) {
      setShowActionGuide(false);
    }
  }, [showActionGuideButton]);

  /* ── load Action Guide for current incident (type+category) ───── */
  useEffect(() => {
         if (!incident || !showActionGuideButton) {
      setGuideText('');
      return;
    }

    const typeForGuide = String(
      incident.srpLabel ||
      incident.incidentTypeName ||
      incident.incidentType ||
      incidentTypeName ||
      ''
    ).trim();

    const catForGuide = String(
      incident.incidentCategory ||
      incidentCategory ||
      ''
    ).trim();

    const accountID = incident.iAccountID;
    const buildingID = String(incident.iBuildingID || iBuildingID || '').trim();

    if (!accountID || !typeForGuide || !catForGuide) {
      setGuideText('');
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        setGuideLoading(true);
        const { data } = await axios.get('/action-guides/one', {
          params: {
            iAccountID: accountID,
            iBuildingID: buildingID,
            incidentType: typeForGuide,
            category: catForGuide,
          },
          withCredentials: true,
        });
        if (!cancelled) setGuideText(data?.guideText || '');
      } catch (e) {
        console.error('load action guide', e);
        if (!cancelled) setGuideText('');
      } finally {
        if (!cancelled) setGuideLoading(false);
      }
    })();

    return () => { cancelled = true; };
    }, [incident, incidentTypeName, incidentCategory, iBuildingID, showActionGuideButton]);



  /* Refresh the announcement and active state when this incident changes. */
  const fetchAdminPost = useCallback(async (signal) => {
    const id = incident?.incidentID;
    if (!id || !Number(incident?.isActive)) return;
    try {
      const { data } = await axios.get(`/ialert/incidents/${id}/admin-post`, { signal });
      if (signal?.aborted) return;
      const nextMsg = String(data?.adminPostMessage || '');
      const nextIsActive = Number(data?.isActive || 0);
      setIncident((prev) => {
        if (!prev || String(prev.incidentID) !== String(id)) return prev;
        if (String(prev.adminPostMessage || '') === nextMsg && Number(prev.isActive || 0) === nextIsActive) return prev;
        return { ...prev, adminPostMessage: nextMsg, isActive: nextIsActive };
      });
    } catch (_) { /* Existing fallback refresh remains available. */ }
  }, [incident?.incidentID, incident?.isActive]);

  useOperationalUpdates({
    client: axios,
    path: `/ialert/incidentmessages/${encodeURIComponent(incident?.incidentID || '')}/live-events`,
    iAccountID: incident?.iAccountID,
    iBuildingID: incident?.iBuildingID,
    app: 'IncidentAlert',
    enabled: Boolean(incident?.incidentID && Number(incident?.isActive)),
    fallbackMs: ADMIN_POST_POLL_MS,
    refreshKey: String(incident?.incidentID || ''),
    onRefresh: fetchAdminPost,
  });

  /* An emergency MFA bypass ends as soon as this incident is closed. */
  useEffect(() => {
    if (!incident?.incidentID || Number(incident.isActive) !== 0) return;

    const accountID = String(
      incident.iAccountID || user?.iAccountID || user?.accountID || ''
    ).trim();
    const userID = String(user?.userID || user?.iUserID || '').trim();

    if (!accountID || !userID) return;

    let cancelled = false;

    axios.get('/authenticate-mfa-session', {
      params: { incidentID: incident.incidentID },
      headers: {
        iAccountID: accountID,
        iUserID: userID,
      },
      withCredentials: true,
    })
      .then(({ data }) => {
        if (cancelled) return;

        const verified = data?.mfaVerified;
        const mode = data?.mfaAuthenticationMode ||
          (verified === true ? 'trusted' : verified === false ? 'required' : 'pending');

        setUser?.((prev) => ({
          ...(prev || {}),
          isMfaAuthenticated:
            verified === true ? true : verified === false ? false : null,
          mfaAuthenticationMode: mode,
          mfaEmergencyIncidentID:
            mode === 'emergency-incident'
              ? String(data?.emergencyIncidentID || data?.incidentID || '')
              : null,
        }));
      })
      .catch((error) => {
        if (!cancelled) {
          console.error('MFA recheck after incident closure failed', error);
          setUser?.((prev) => ({
            ...(prev || {}),
            isMfaAuthenticated: false,
            mfaAuthenticationMode: 'required',
            mfaEmergencyIncidentID: null,
          }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    incident?.incidentID,
    incident?.isActive,
    incident?.iAccountID,
    user?.iAccountID,
    user?.accountID,
    user?.userID,
    user?.iUserID,
    setUser,
  ]);


  /* Keep creator status current through the same shared incident stream. */
  const loadCreatorStatus = useCallback(async (signal) => {
    if (!incident?.incidentID) return;
    try {
      const { data } = await axios.get(`/ialert/incidentmessages/${incident.incidentID}`, { signal });
      if (signal?.aborted) return;
      const latest = (data?.messages || [])
        .filter(m =>
          String(m.senderUserID || '') === String(incident.createdByUserID || '') &&
          m.myStatus &&
          (Array.isArray(m.myStatus) ? m.myStatus.length : String(m.myStatus).trim())
        )
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
      if (latest) setCreatorStatus(Array.isArray(latest.myStatus) ? latest.myStatus : parseStatus(latest.myStatus));
    } catch (error) {
      if (!signal?.aborted && error?.code !== 'ERR_CANCELED') console.error('load creator status', error);
    }
  }, [incident?.incidentID, incident?.createdByUserID]);

  useOperationalUpdates({
    client: axios,
    path: `/ialert/incidentmessages/${encodeURIComponent(incident?.incidentID || '')}/live-events`,
    iAccountID: incident?.iAccountID,
    iBuildingID: incident?.iBuildingID,
    app: 'IncidentAlert',
    enabled: Boolean(incident?.incidentID),
    fallbackMs: 15000,
    refreshKey: `${incident?.incidentID || ''}:${incident?.createdByUserID || ''}`,
    onRefresh: loadCreatorStatus,
  });

  /* ---------- update myStatus (per-user) ------------------------- */
  const updateMyStatus = useCallback(async selArr => {
    setStatusArr(selArr); // local UI

    // persist (survives refresh)
    try {
      if (statusStorageKey) {
        sessionStorage.setItem(statusStorageKey, JSON.stringify(selArr || []));
      }
    } catch {
      // ignore storage errors
    }



    const fullName  = `${user.firstName} ${user.lastName}`.trim();
    const statusLine = selArr.join(', ') || '(cleared)';

    const geo = await new Promise(resolve => {
      if (!navigator.geolocation) return resolve({});
      navigator.geolocation.getCurrentPosition(
        p => resolve({ latitude: p.coords.latitude, longitude: p.coords.longitude, accuracy: p.coords.accuracy }),
        () => resolve({}),
        { enableHighAccuracy:true, maximumAge:30_000 }
      );
    });

    try {
      await axios.post(`/ialert/incidentmessages/${incident.incidentID}`, {
        iAccountID: incident.iAccountID,
        iCampusID : incident.iCampusID,
        iBuildingID: incident.iBuildingID,
        senderUserID: user.userID,
        senderName: fullName,
        senderMobileNumber: user.userMobileNumber,
        messageText: statusLine,
        myStatus: selArr,
        ...geo,
      });
    } catch (e) { console.error('update myStatus', e); }
  }, [incident, user, statusStorageKey]);

  /* toggle Active / Inactive */
  const toggleActive = useCallback(async () => {
    try {
      const { data } = await axios.patch(
        `/ialert/incidents/${incident.incidentID}/active`,
        { isActive: incident.isActive ? 0 : 1 },
      );
      setIncident(data);
    } catch (e) {
      console.error('toggle active', e);
      alert('Failed to update incident state');
    }
  }, [incident]);

  if (!incident) return null;
  // DRILL overlay only when viewing an incident that is actually DRILL
  const isDrillIncident = String(incident?.incidentMode || '').toUpperCase() === 'DRILL';

  const bannerColour  = srpColour(incident.srpLabel ?? incident.incidentType);
  const phoneNumber   = incident.createdByMobileNumber || '';
  const safeCreatorStatus = Array.isArray(creatorStatus) ? creatorStatus : [];
  const safeStatusArr     = Array.isArray(statusArr)     ? statusArr     : [];
  // pulse reminders (independent)
  const needsStatus = safeStatusArr.filter(s => String(s || '').trim()).length === 0;
  const needsLocation = !String(incident?.myLocation || '').trim();
 
  /* ---------- render -------------------------------------------- */
  return (
    <Container fluid className="p-0" style={{ height: '100vh' }}>

      {/* Incident-scoped DRILL watermark (beneath any modal, above page content) */}
      {isDrillIncident && (
        <div
          aria-hidden="true"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1040,          // below Bootstrap modal (1050), above content
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: 0.1,
            fontSize: '12vw',
            fontWeight: 900,
            color: 'green',
            textTransform: 'uppercase',
            userSelect: 'none',
            transform: 'rotate(-30deg)',
          }}
        >
          This&nbsp;is&nbsp;a&nbsp;Drill
        </div>
      )}

      <Card className="d-flex flex-column h-100 w-100 border-3"
            style={{ borderColor: '#f2c94c', borderRadius: 0, fontSize: 13, position:'relative' }}>
 

        {/* Title */}
        <DashboardCardHeader
          title={
            <div className="d-flex align-items-center gap-2">
              <span>{incident.accountName || 'Status & Messages'}</span>
              <Button
                variant="warning"
                size="sm"
                className="rounded-circle d-inline-flex align-items-center justify-content-center fw-bold"
                style={{
                  width: 28, height: 28, lineHeight: 1,
                  backgroundColor: 'darkblue', color: '#fff',
                  border: '2px solid white', boxShadow: '0 0 6px rgba(255,87,34,0.6)',
                  transition: 'all 0.2s ease-in-out'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#ff5722';
                  e.currentTarget.style.borderColor = '#d32f2f';
                  e.currentTarget.style.boxShadow = '0 0 10px rgba(211,47,47,0.8)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'darkblue';
                  e.currentTarget.style.borderColor = '#fff';
                  e.currentTarget.style.boxShadow = '0 0 6px rgba(255,87,34,0.6)';
                }}
                onClick={() => setShowHelp(true)}
                aria-label="How to use the Active Incident page"
                title="How to use this page"
              >
                ?
              </Button>
            </div>
          }
          closePath="/reportincidentpanel"
          hideDropdown
          logoOverride={logo}
        />

        {/* body */}
        <Card.Body className={`d-flex flex-column ${activeStyles.bodyTight}`}
                   style={{ flex: 1, minHeight: 0, overflow: 'hidden', position:'relative', zIndex:1 }}>
          {/* quick-action buttons */}
          {/* BUTTON PANEL (responsive grid) */}

 
 <div
  className={`${activeStyles.buttonPanel} ${
    isAlertUser ? activeStyles.alertUserButtonPanel : ''
  } ${isAdmin ? activeStyles.adminButtonPanel : ''}`}
>
<Button
  variant="outline-danger"
 className={`${activeStyles.panelBtn} ${activeStyles.adminMobilePrimaryBtn} ${needsStatus ? activeStyles.pulseDanger : ''}`}
  onClick={() => setShowStatusModal(true)}
  aria-label="Update My Status"
>
    <ExclamationTriangleFill size={18} />
    <span className={activeStyles.btnText}>Update My Status</span>
  </Button>

<Button
  variant="outline-primary"
 className={`${activeStyles.panelBtn} ${activeStyles.adminMobilePrimaryBtn} ${needsLocation ? activeStyles.pulsePrimary : ''}`}
  onClick={() => setShowLocModal(true)}
  aria-label="Update My Location"
>
    <GeoAltFill size={18} />
    <span className={activeStyles.btnText}>Update My Location</span>
  </Button>

   {showActionGuideButton && (
    <Button
      variant="outline-secondary"
      className={`${activeStyles.panelBtn} ${activeStyles.adminMobileSmallBtn}`}
      onClick={() => setShowActionGuide(true)}
      title="Action Guide"
      aria-label="Action Guide"
    >
      <PatchExclamationFill size={18} />
      <span className={activeStyles.btnText}>Action Guide</span>
    </Button>
  )}


 {isAdmin && (
  <Button
    variant="primary"
     className={`${activeStyles.panelBtn} ${activeStyles.adminMobileSmallBtn}`}
    onClick={() =>
      navigate('/dashboard', {
        state: {
          selectedIncident: incident,
          incident: incident, // keep both for safety/backward compatibility
          iCampusID: incident?.iCampusID ?? '-99',
          iBuildingID: incident?.iBuildingID ?? '-99',
          activeOnly: '1',
          fromActiveIncident: true,
        },
      })
    }
    title="Incident Monitor"
    aria-label="Incident Monitor"
  >
    <Power size={18} />
    <span className={activeStyles.btnText}>Incident Monitor</span>
  </Button>
)}


  {isAdmin && (
    <Button
      variant="outline-secondary"
       className={`${activeStyles.panelBtn} ${activeStyles.adminMobileSmallBtn}`}
      onClick={() => setShowLocEditor(true)}
      title="Administration"
      aria-label="Admin"
    >
      <GearFill size={18} />
      <span className={activeStyles.btnText}>Manage Incident</span>
    </Button>
  )}

</div>

          {/* banner + meta */}
          <ListGroup variant="flush" className="mb-1" style={{ border:'5px solid gold', marginTop:'3px'}}>
 

 <ListGroup.Item
  className={activeStyles.bannerItem}
  style={{ background: `var(--bs-${bannerColour})` }}
>
  <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center w-100 gap-0">
    <div className="d-flex align-items-center gap-0 flex-wrap w-100">
      <ExclamationTriangleFill className={activeStyles.bannerIcon} />

      <span className={activeStyles.bannerText}>
        {incident.srpLabel || incidentTypeName || '—'} {' — '} {incidentCategory}
      </span>

      <div className={activeStyles.modeCenterWrapper}>
        <div className={activeStyles.modeInner}>
          <strong className={activeStyles.modeLabel}>Mode:</strong>
          <Badge
            bg={isDrillIncident ? 'success' : 'primary'}
            className={activeStyles.modeBadge}
          >
            {incident.incidentMode}
          </Badge>
        </div>
      </div>
    </div>

  </div>
</ListGroup.Item>

            {/* ADMIN MESSAGE BAR (RED) */}
            {(() => {
              const msg = String(incident?.adminPostMessage || '').trim();
              const needsExpand = msg.length > 90 || msg.includes('\n');

              if (!msg && !isAdmin) return null;

              return (
                <ListGroup.Item className={activeStyles.adminMsgRow}>
                  <div className={activeStyles.adminMsgText} title={msg}>
                  {needsExpand ? (
                    <Button
                      size="sm"
                      variant="light"
                      className={activeStyles.adminMsgExpandBtn}
                      onClick={() => setShowAdminPost(true)}
                      title="Expand message"
                    >
                      <ArrowsAngleExpand size={14} />
                    </Button>
                  ) : null}  {msg || ' '}
                  </div>

                  {/* {needsExpand ? (
                    <Button
                      size="sm"
                      variant="light"
                      className={activeStyles.adminMsgExpandBtn}
                      onClick={() => setShowAdminPost(true)}
                      title="Expand message"
                    >
                      <ArrowsAngleExpand size={14} />
                    </Button>
                  ) : null} */}
                </ListGroup.Item>
              );
            })()}
 
 {/* <ListGroup.Item className="py-1 px-2">
  <GeoAltFill className="me-2" />
  <span>
    {incident.locationName || incident.buildingName || '—'}
    {incident.myLocation ? ` | ${incident.myLocation}` : ''}
  </span>
</ListGroup.Item> */}

<ListGroup.Item className="py-1 px-2">
  <GeoAltFill className="me-2" />
  <span>
    {incident.locationName || incident.buildingName || '—'}
    {incident.myLocation ? ` | Location: ${incident.myLocation}` : ''}
    {safeStatusArr.length > 0 ? ` | Status: ${safeStatusArr.join(', ')}` : ''}
  </span>
</ListGroup.Item>

            <ListGroup.Item
              className="py-1 px-2 d-flex justify-content-between align-items-center"
              style={{ background: '#eee' }}>
              {/* LEFT: phone icon + name + status */}
              <span className="d-flex align-items-center gap-2">
                {phoneNumber && (
                  <a href={`tel:${phoneNumber}`} title={`Call ${phoneNumber}`}>
                    <TelephoneFill size={16}/>
                  </a>
                )}
                <span>
                  <b>By</b> {incident.createdByName || '—'}
                  {safeCreatorStatus.length > 0 && (<> – <em>{safeCreatorStatus.join(', ')}</em></>)}
                </span>
              </span>

              {/* RIGHT: live badge */}
              <div className="d-flex align-items-center gap-2">
                <Badge bg={incident.isActive ? 'success' : 'secondary'} className="px-2">
                  {incident.isActive ? 'ACTIVE' : 'INACTIVE'}
                </Badge>
              </div>
            </ListGroup.Item>
          </ListGroup>

          {/* chat */}
          <div className="flex-grow-1 d-flex flex-column border rounded"
            style={{ minHeight: 0, overflow: 'hidden' }}>
            <IncidentAlertChat
              incidentID={incident.incidentID}
              iAccountID={incident.iAccountID}
              iCampusID={incident.iCampusID}
              iBuildingID={incident.iBuildingID}
              buildingName={incident.locationName || incident.buildingName || '—'}
              myLocation={incident.myLocation}
              myStatus={safeStatusArr}
            />
          </div>
        </Card.Body>
      </Card>

      {/* Help modal */}
      <Modal show={showHelp} onHide={() => setShowHelp(false)} centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title>How to use the Active Incident page</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ol className="mb-0 ps-3">
            <li><b>Review the banner</b> for incident type, category, and <b>Mode</b> (DRILL or REAL).</li>
            <li><b>Update My Status</b> to share your current condition; this also posts a message to the incident thread (geo is included when available).</li>
            <li><b>Update Room Location</b> to add your room/area (admins can use the gear to edit the location list).</li>
            <li>Use the <b>chat</b> area below to read/post messages tied to this incident.</li>
            <li>If needed, tap the <b>phone icon</b> to call the incident creator’s number.</li>
            <li>The footer row shows who created the incident and their latest status; the badge indicates if the incident is <b>Active</b> or <b>Inactive</b>.</li>
            {isAdmin && <li><b>Admins:</b> use the gear to open the Location Editor; toggle Open/Close incident is available in admin tools (where enabled).</li>}
          </ol>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowHelp(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* modals */}

<Modal show={showAdminPost} onHide={() => setShowAdminPost(false)} centered>
  <Modal.Header closeButton>
    <Modal.Title>Admin Message</Modal.Title>
  </Modal.Header>
  <Modal.Body style={{ fontSize: 18, fontWeight: 700 }}>
    {String(incident?.adminPostMessage || '').trim() || '—'}
  </Modal.Body>
  <Modal.Footer>
    <Button variant="primary" onClick={() => setShowAdminPost(false)}>Close</Button>
  </Modal.Footer>
</Modal>



            <ActionGuideModal
        show={showActionGuide}
        onHide={() => setShowActionGuide(false)}
        loading={guideLoading}
        titleText={`${incident.srpLabel || incidentTypeName || incident.incidentType || '—'} — ${incident.incidentCategory || incidentCategory || ''}`}
        guideText={guideText}
      />

 <ReportStatusModal
  show={showStatusModal}
  onHide={() => setShowStatusModal(false)}
  onSave={updateMyStatus}
  initial={statusArr}
/>

      <IncidentTypeModal
        show   ={showIncidentModal}
        onHide ={()=>setShowIncidentModal(false)}
        onSave  ={({ incidentType, category }) => {
          setIncidentTypeName(incidentType);
          setIncidentCategory(category);
        }}
      />
      <UpdateLocationModal
        show={showLocModal}
        onHide={() => setShowLocModal(false)}
        incident={incident}
        userContext={{
          user,
          iAccountID : user?.iAccountID,
          iBuildingID: iBuildingID,
          iCampusID  : incident?.iCampusID,
        }}
        onSaved={(loc) => {
  const next = String(loc || '');
  setIncident(prev => (prev ? { ...prev, myLocation: next } : prev));

  // persist (survives refresh)
  try {
    if (locStorageKey) sessionStorage.setItem(locStorageKey, next);
  } catch {
    // ignore storage errors
  }
}}
      />
           <LocationEditorModal
        show={showLocEditor}
        onHide={() => setShowLocEditor(false)}
        incident={incident}
        userContext={{
          user,
          iAccountID : user?.iAccountID,
          iBuildingID: iBuildingID,
          iCampusID: incident?.iCampusID,
        }}
        toggleActive={toggleActive}
        onIncidentChange={(row) => {
          setIncident(prev => ({ ...(prev || {}), ...(row || {}) }));
        }}
        onActionGuideVisibilityChange={(nextValue) => {
          const next = !!nextValue;
          setShowActionGuideButton(next);
          if (!next) setShowActionGuide(false);
        }}
      />



    </Container>
  );
}




 
 
// /*  ActiveIncidentPage.jsx – full file */
// import React, { useState, useEffect, useCallback, useContext } from 'react';
// import {
//   Card, Container, Row, Col, Button, Badge, ListGroup, Modal
// } from 'react-bootstrap';
// import { useLocation, useNavigate, useParams } from 'react-router-dom';
// import {
//   ExclamationTriangleFill,
//   GeoAltFill,
//   GearFill,
//   PatchExclamationFill,
//   TelephoneFill,
//   Power,
//   ArrowsAngleExpand,
// } from 'react-bootstrap-icons';

// import IncidentAlertChat    from './IncidentAlertChat';
// import DashboardCardHeader  from '../ViewReports/DashboardCardHeader';
// import ReportStatusModal    from './ReportStatusModal';
// import IncidentTypeModal    from './IncidentTypeModal';
// import UpdateLocationModal  from './UpdateLocationModal';
// import LocationEditorModal  from './LocationEditorModal';
// import ActionGuideModal     from './ActionGuideModal';
// import logo                 from '../ViewReports/SchoolShield_color300x3xx.webp';

// import axios, {
//   setInactiveIncidentWriteGate,
//   clearInactiveIncidentWriteGate,
// } from '../axiosAlertConfig';

// import { UserContext }      from '../../userAlertContext';
// import activeStyles from './ActiveIncidentPage.module.css';


// /* ---------- helpers --------------------------------------------- */
// const srpColour = (txt = '') => {
//   const k = txt.toLowerCase();
//   if (k.includes('lock'))    return 'danger';
//   if (k.includes('evac'))    return 'warning';
//   if (k.includes('secure'))  return 'primary';
//   if (k.includes('shelter')) return 'info';
//   if (k.includes('hold'))    return 'secondary';
//   return 'dark';
  
// };

// const parseStatus = raw => {
//   if (!raw)           return [];
//   if (Array.isArray(raw)) return raw;
//   try   { return JSON.parse(raw); }
//   catch { return [String(raw)]; }
// };

// /* ---------- component ------------------------------------------- */
// export default function ActiveIncidentPage() {
//   const { state }      = useLocation();
//   const { incidentId } = useParams();
//   const navigate       = useNavigate();
//   const { user, iBuildingID } = useContext(UserContext);
  

//   /* main incident */
//   const [incident, setIncident] = useState(state?.incident || null);

//   /* UI state */
//   const [showStatusModal,   setShowStatusModal]   = useState(false);
//   const [showIncidentModal, setShowIncidentModal] = useState(false);
//   const [showHelp,          setShowHelp]          = useState(false);

//    const [showActionGuide, setShowActionGuide] = useState(false);
//   const [showActionGuideButton, setShowActionGuideButton] = useState(false);
//   const [guideLoading, setGuideLoading] = useState(false);
//   const [guideText, setGuideText] = useState('');



//   const [incidentTypeName,  setIncidentTypeName ] = useState(incident?.incidentType || '');
//   const [incidentCategory,  setIncidentCategory]  = useState(incident?.incidentCategory || '');
//   const [statusArr,         setStatusArr]         = useState(parseStatus(incident?.myStatus));
//   const [creatorStatus,     setCreatorStatus]     = useState(parseStatus(incident?.myStatus));

//   // location state
//   const [showLocModal,  setShowLocModal]  = useState(false);
//   const [showLocEditor, setShowLocEditor] = useState(false);
//   const [myLocation,    setMyLocation]    = useState(incident?.myLocation || '');
// const [showAdminPost, setShowAdminPost] = useState(false);
// // Gate pulses until we've loaded server values at least once
// const [statusReady, setStatusReady] = useState(false);
// const [locationReady, setLocationReady] = useState(false);

//   const isAdmin = ['administrator','systemadministrator']
//                     .includes((user?.userRole || '').toLowerCase());

//    const isAlertUser = ['alertuser', 'alertusers']
//   .includes((user?.userRole || '').toLowerCase());                 

//   const AUTO_SUPPRESS_MS = 3000; // keep in sync with panel
//   const ADMIN_POST_POLL_MS = 10000; // refresh adminPostMessage every 5s while incident is active

//   // ---- per-user persistence (survives refresh) -------------------
//   const incidentKey = String(incidentId || incident?.incidentID || '');
//   const userKey = String(user?.userID || '');
//   const statusStorageKey = incidentKey && userKey ? `aip:${incidentKey}:${userKey}:statusArr` : '';
//   const locStorageKey    = incidentKey && userKey ? `aip:${incidentKey}:${userKey}:myLocation` : '';

//   const hasNonEmptyStatus = (arr) =>
//     Array.isArray(arr) && arr.some(s => String(s || '').trim());

//   const actionGuideAccountID = String(incident?.iAccountID || user?.iAccountID || '').trim();

//   // Mark we entered Active; on unmount, give the panel a small TTL to avoid bounce.
//   useEffect(() => {
//     sessionStorage.setItem('activeOpenAt', String(Date.now()));
//     return () => {
//       sessionStorage.setItem(
//         'suppressAutoOpenUntil',
//         String(Date.now() + AUTO_SUPPRESS_MS)
//       );
//     };
//   }, []);

//   /* fetch on hard refresh / deep-link */
//   useEffect(() => {
//     if (incident || !incidentId) return;
//     (async () => {
//       try {
//         const { data } = await axios.get(`/ialert/incidents/${incidentId}`);
//         if (data) {
//           const obj = Array.isArray(data) ? data[0] : data;
//           setIncident(obj);
//           setStatusArr(parseStatus(obj.myStatus));
//         }
//       } catch (e) { console.error('load incident', e); }
//     })();
//   }, [incident, incidentId]);


//   /* keep axios write gate synced to this incident */
//   useEffect(() => {
//     if (!incident?.incidentID) {
//       clearInactiveIncidentWriteGate();
//       return;
//     }

//     setInactiveIncidentWriteGate({
//       incidentID: incident.incidentID,
//       isActive: incident.isActive,
//     });

//     return () => {
//       clearInactiveIncidentWriteGate();
//     };
//   }, [incident?.incidentID, incident?.isActive]);



//   // ---- rehydrate status (per user) after refresh ------------------
//   useEffect(() => {
//     if (!statusStorageKey) return;

//     // If we already have status, don't overwrite it
//     if (hasNonEmptyStatus(statusArr)) return;

//     try {
//       const raw = sessionStorage.getItem(statusStorageKey);
//       if (!raw) return;

//       const saved = parseStatus(raw);
//       if (hasNonEmptyStatus(saved)) setStatusArr(saved);
//     } catch {
//       // ignore storage errors
//     }
//   }, [statusStorageKey, incident?.incidentID, statusArr]);

//   // ---- rehydrate location (per user) after refresh ----------------
//   useEffect(() => {
//     if (!locStorageKey) return;
//     if (!incident) return;

//     const current = String(incident?.myLocation || '').trim();
//     if (current) return;

//     try {
//       const saved = String(sessionStorage.getItem(locStorageKey) || '').trim();
//       if (!saved) return;

//       setIncident(prev => {
//         if (!prev) return prev;
//         const prevLoc = String(prev.myLocation || '').trim();
//         if (prevLoc) return prev;
//         return { ...prev, myLocation: saved };
//       });
//     } catch {
//       // ignore storage errors
//     }
//   }, [locStorageKey, incident, incident?.myLocation]);




//  useEffect(() => {
//    // if (incident || !incidentId) return;
     
// //console.log('[ACTIVEINCIDENTPAGE] INCIDENT ',incident )


//   }, [incident, incidentId]);


//   /* ── load Action Guide button visibility (account-level) ───────── */
//   useEffect(() => {
//     if (!actionGuideAccountID) {
//       setShowActionGuideButton(false);
//       return;
//     }

//     let cancelled = false;

//     (async () => {
//       try {
//         const { data } = await axios.get('/action-guides/action-guide-button-visibility', {
//           params: { iAccountID: actionGuideAccountID },
//           withCredentials: true,
//         });

//         if (!cancelled) {
//           setShowActionGuideButton(!!data?.showActionGuideButton);
//         }
//       } catch (e) {
//         console.error('load action guide button visibility', e);
//         if (!cancelled) setShowActionGuideButton(false);
//       }
//     })();

//     return () => { cancelled = true; };
//   }, [actionGuideAccountID]);

//   useEffect(() => {
//     if (!showActionGuideButton) {
//       setShowActionGuide(false);
//     }
//   }, [showActionGuideButton]);

//   /* ── load Action Guide for current incident (type+category) ───── */
//   useEffect(() => {
//          if (!incident || !showActionGuideButton) {
//       setGuideText('');
//       return;
//     }

//     const typeForGuide = String(
//       incident.srpLabel ||
//       incident.incidentTypeName ||
//       incident.incidentType ||
//       incidentTypeName ||
//       ''
//     ).trim();

//     const catForGuide = String(
//       incident.incidentCategory ||
//       incidentCategory ||
//       ''
//     ).trim();

//     const accountID = incident.iAccountID;
//     const buildingID = String(incident.iBuildingID || iBuildingID || '').trim();

//     if (!accountID || !typeForGuide || !catForGuide) {
//       setGuideText('');
//       return;
//     }

//     let cancelled = false;

//     (async () => {
//       try {
//         setGuideLoading(true);
//         const { data } = await axios.get('/action-guides/one', {
//           params: {
//             iAccountID: accountID,
//             iBuildingID: buildingID,
//             incidentType: typeForGuide,
//             category: catForGuide,
//           },
//           withCredentials: true,
//         });
//         if (!cancelled) setGuideText(data?.guideText || '');
//       } catch (e) {
//         console.error('load action guide', e);
//         if (!cancelled) setGuideText('');
//       } finally {
//         if (!cancelled) setGuideLoading(false);
//       }
//     })();

//     return () => { cancelled = true; };
//     }, [incident, incidentTypeName, incidentCategory, iBuildingID, showActionGuideButton]);



//   /* ── poll adminPostMessage (updates banner text live) ───────────── */
//   useEffect(() => {
//     if (!incident?.incidentID) return;

//     // Only poll while this incident is active (your requirement: "particular active incident")
//     if (!Number(incident?.isActive)) return;

//     const id = incident.incidentID;
//     let cancelled = false;

//     const fetchAdminPost = async () => {
//       try {
//         const { data } = await axios.get(`/ialert/incidents/${id}/admin-post`);

         
//         const nextMsg = String(data?.adminPostMessage || '');

//         if (cancelled) return;

//         // IMPORTANT: only update adminPostMessage (do NOT overwrite the incident object)
//         setIncident((prev) => {
//           if (!prev) return prev;

//           const prevMsg = String(prev.adminPostMessage || '');
//           if (prevMsg === nextMsg) return prev;

//           return { ...prev, adminPostMessage: nextMsg };
//         });
//       } catch (e) {
//         // keep quiet; polling should not spam console
//       }
//     };

//     // initial fetch + interval
//     fetchAdminPost();
//     const t = setInterval(fetchAdminPost, ADMIN_POST_POLL_MS);

//     return () => {
//       cancelled = true;
//       clearInterval(t);
//     };
//   }, [incident?.incidentID, incident?.isActive]);


//   /* ── keep the creator’s status in-sync (15 s poll) ─────────────── */
//   useEffect(() => {
//     if (!incident) return;
//     const loadCreatorStatus = async () => {
//       try {
//         const { data } = await axios.get(`/ialert/incidentmessages/${incident.incidentID}`);
//         const msgs = data?.messages || [];
//         const latest = msgs
//           .filter(m =>
//             (m.senderUserID || '') === incident.createdByUserID &&
//             m.myStatus &&
//             (Array.isArray(m.myStatus) ? m.myStatus.length : String(m.myStatus).trim())
//           )
//           .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
//         if (latest) {
//           setCreatorStatus(Array.isArray(latest.myStatus) ? latest.myStatus : parseStatus(latest.myStatus));
//         }
//       } catch (e) {
//         console.error('poll creator status', e);
//       }
//     };
//     loadCreatorStatus();
//     const id = setInterval(loadCreatorStatus, 15_000);
//     return () => clearInterval(id);
//   }, [incident]);

//   /* ---------- update myStatus (per-user) ------------------------- */
//   const updateMyStatus = useCallback(async selArr => {
//     setStatusArr(selArr); // local UI

//     // persist (survives refresh)
//     try {
//       if (statusStorageKey) {
//         sessionStorage.setItem(statusStorageKey, JSON.stringify(selArr || []));
//       }
//     } catch {
//       // ignore storage errors
//     }



//     const fullName  = `${user.firstName} ${user.lastName}`.trim();
//     const statusLine = selArr.join(', ') || '(cleared)';

//     const geo = await new Promise(resolve => {
//       if (!navigator.geolocation) return resolve({});
//       navigator.geolocation.getCurrentPosition(
//         p => resolve({ latitude: p.coords.latitude, longitude: p.coords.longitude, accuracy: p.coords.accuracy }),
//         () => resolve({}),
//         { enableHighAccuracy:true, maximumAge:30_000 }
//       );
//     });

//     try {
//       await axios.post(`/ialert/incidentmessages/${incident.incidentID}`, {
//         iAccountID: incident.iAccountID,
//         iCampusID : incident.iCampusID,
//         iBuildingID: incident.iBuildingID,
//         senderUserID: user.userID,
//         senderName: fullName,
//         senderMobileNumber: user.userMobileNumber,
//         messageText: statusLine,
//         myStatus: selArr,
//         ...geo,
//       });
//     } catch (e) { console.error('update myStatus', e); }
//   }, [incident, user, statusStorageKey]);

//   /* toggle Active / Inactive */
//   const toggleActive = useCallback(async () => {
//     try {
//       const { data } = await axios.patch(
//         `/ialert/incidents/${incident.incidentID}/active`,
//         { isActive: incident.isActive ? 0 : 1 },
//       );
//       setIncident(data);
//     } catch (e) {
//       console.error('toggle active', e);
//       alert('Failed to update incident state');
//     }
//   }, [incident]);

//   if (!incident) return null;
//   // DRILL overlay only when viewing an incident that is actually DRILL
//   const isDrillIncident = String(incident?.incidentMode || '').toUpperCase() === 'DRILL';

//   const bannerColour  = srpColour(incident.srpLabel ?? incident.incidentType);
//   const phoneNumber   = incident.createdByMobileNumber || '';
//   const safeCreatorStatus = Array.isArray(creatorStatus) ? creatorStatus : [];
//   const safeStatusArr     = Array.isArray(statusArr)     ? statusArr     : [];
//   // pulse reminders (independent)
//   const needsStatus = safeStatusArr.filter(s => String(s || '').trim()).length === 0;
//   const needsLocation = !String(incident?.myLocation || '').trim();
 
//   /* ---------- render -------------------------------------------- */
//   return (
//     <Container fluid className="p-0" style={{ height: '100vh' }}>

//       {/* Incident-scoped DRILL watermark (beneath any modal, above page content) */}
//       {isDrillIncident && (
//         <div
//           aria-hidden="true"
//           style={{
//             position: 'fixed',
//             inset: 0,
//             zIndex: 1040,          // below Bootstrap modal (1050), above content
//             pointerEvents: 'none',
//             display: 'flex',
//             alignItems: 'center',
//             justifyContent: 'center',
//             opacity: 0.1,
//             fontSize: '12vw',
//             fontWeight: 900,
//             color: 'green',
//             textTransform: 'uppercase',
//             userSelect: 'none',
//             transform: 'rotate(-30deg)',
//           }}
//         >
//           This&nbsp;is&nbsp;a&nbsp;Drill
//         </div>
//       )}

//       <Card className="d-flex flex-column h-100 w-100 border-3"
//             style={{ borderColor: '#f2c94c', borderRadius: 0, fontSize: 13, position:'relative' }}>
 

//         {/* Title */}
//         <DashboardCardHeader
//           title={
//             <div className="d-flex align-items-center gap-2">
//               <span>{incident.accountName || 'Status & Messages'}</span>
//               <Button
//                 variant="warning"
//                 size="sm"
//                 className="rounded-circle d-inline-flex align-items-center justify-content-center fw-bold"
//                 style={{
//                   width: 28, height: 28, lineHeight: 1,
//                   backgroundColor: 'darkblue', color: '#fff',
//                   border: '2px solid white', boxShadow: '0 0 6px rgba(255,87,34,0.6)',
//                   transition: 'all 0.2s ease-in-out'
//                 }}
//                 onMouseEnter={(e) => {
//                   e.currentTarget.style.backgroundColor = '#ff5722';
//                   e.currentTarget.style.borderColor = '#d32f2f';
//                   e.currentTarget.style.boxShadow = '0 0 10px rgba(211,47,47,0.8)';
//                 }}
//                 onMouseLeave={(e) => {
//                   e.currentTarget.style.backgroundColor = 'darkblue';
//                   e.currentTarget.style.borderColor = '#fff';
//                   e.currentTarget.style.boxShadow = '0 0 6px rgba(255,87,34,0.6)';
//                 }}
//                 onClick={() => setShowHelp(true)}
//                 aria-label="How to use the Active Incident page"
//                 title="How to use this page"
//               >
//                 ?
//               </Button>
//             </div>
//           }
//           closePath="/reportincidentpanel"
//           hideDropdown
//           logoOverride={logo}
//         />

//         {/* body */}
//         <Card.Body className={`d-flex flex-column ${activeStyles.bodyTight}`}
//                    style={{ flex: 1, minHeight: 0, overflow: 'hidden', position:'relative', zIndex:1 }}>
//           {/* quick-action buttons */}
//           {/* BUTTON PANEL (responsive grid) */}

 
//  <div
//   className={`${activeStyles.buttonPanel} ${
//     isAlertUser ? activeStyles.alertUserButtonPanel : ''
//   } ${isAdmin ? activeStyles.adminButtonPanel : ''}`}
// >
// <Button
//   variant="outline-danger"
//  className={`${activeStyles.panelBtn} ${activeStyles.adminMobilePrimaryBtn} ${needsStatus ? activeStyles.pulseDanger : ''}`}
//   onClick={() => setShowStatusModal(true)}
//   aria-label="Update My Status"
// >
//     <ExclamationTriangleFill size={18} />
//     <span className={activeStyles.btnText}>Update My Status</span>
//   </Button>

// <Button
//   variant="outline-primary"
//  className={`${activeStyles.panelBtn} ${activeStyles.adminMobilePrimaryBtn} ${needsLocation ? activeStyles.pulsePrimary : ''}`}
//   onClick={() => setShowLocModal(true)}
//   aria-label="Update My Location"
// >
//     <GeoAltFill size={18} />
//     <span className={activeStyles.btnText}>Update My Location</span>
//   </Button>

//    {showActionGuideButton && (
//     <Button
//       variant="outline-secondary"
//       className={`${activeStyles.panelBtn} ${activeStyles.adminMobileSmallBtn}`}
//       onClick={() => setShowActionGuide(true)}
//       title="Action Guide"
//       aria-label="Action Guide"
//     >
//       <PatchExclamationFill size={18} />
//       <span className={activeStyles.btnText}>Action Guide</span>
//     </Button>
//   )}


//  {isAdmin && (
//   <Button
//     variant="primary"
//      className={`${activeStyles.panelBtn} ${activeStyles.adminMobileSmallBtn}`}
//     onClick={() =>
//       navigate('/dashboard', {
//         state: {
//           selectedIncident: incident,
//           incident: incident, // keep both for safety/backward compatibility
//           iCampusID: incident?.iCampusID ?? '-99',
//           iBuildingID: incident?.iBuildingID ?? '-99',
//           activeOnly: '1',
//           fromActiveIncident: true,
//         },
//       })
//     }
//     title="Incident Monitor"
//     aria-label="Incident Monitor"
//   >
//     <Power size={18} />
//     <span className={activeStyles.btnText}>Incident Monitor</span>
//   </Button>
// )}


//   {isAdmin && (
//     <Button
//       variant="outline-secondary"
//        className={`${activeStyles.panelBtn} ${activeStyles.adminMobileSmallBtn}`}
//       onClick={() => setShowLocEditor(true)}
//       title="Administration"
//       aria-label="Admin"
//     >
//       <GearFill size={18} />
//       <span className={activeStyles.btnText}>Manage Incident</span>
//     </Button>
//   )}

// </div>

//           {/* banner + meta */}
//           <ListGroup variant="flush" className="mb-1" style={{ border:'5px solid gold', marginTop:'3px'}}>
 

//  <ListGroup.Item
//   className={activeStyles.bannerItem}
//   style={{ background: `var(--bs-${bannerColour})` }}
// >
//   <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center w-100 gap-0">
//     <div className="d-flex align-items-center gap-0 flex-wrap w-100">
//       <ExclamationTriangleFill className={activeStyles.bannerIcon} />

//       <span className={activeStyles.bannerText}>
//         {incident.srpLabel || incidentTypeName || '—'} {' — '} {incidentCategory}
//       </span>

//       <div className={activeStyles.modeCenterWrapper}>
//         <div className={activeStyles.modeInner}>
//           <strong className={activeStyles.modeLabel}>Mode:</strong>
//           <Badge
//             bg={isDrillIncident ? 'success' : 'primary'}
//             className={activeStyles.modeBadge}
//           >
//             {incident.incidentMode}
//           </Badge>
//         </div>
//       </div>
//     </div>

//   </div>
// </ListGroup.Item>

//             {/* ADMIN MESSAGE BAR (RED) */}
//             {(() => {
//               const msg = String(incident?.adminPostMessage || '').trim();
//               const needsExpand = msg.length > 90 || msg.includes('\n');

//               if (!msg && !isAdmin) return null;

//               return (
//                 <ListGroup.Item className={activeStyles.adminMsgRow}>
//                   <div className={activeStyles.adminMsgText} title={msg}>
//                   {needsExpand ? (
//                     <Button
//                       size="sm"
//                       variant="light"
//                       className={activeStyles.adminMsgExpandBtn}
//                       onClick={() => setShowAdminPost(true)}
//                       title="Expand message"
//                     >
//                       <ArrowsAngleExpand size={14} />
//                     </Button>
//                   ) : null}  {msg || ' '}
//                   </div>

//                   {/* {needsExpand ? (
//                     <Button
//                       size="sm"
//                       variant="light"
//                       className={activeStyles.adminMsgExpandBtn}
//                       onClick={() => setShowAdminPost(true)}
//                       title="Expand message"
//                     >
//                       <ArrowsAngleExpand size={14} />
//                     </Button>
//                   ) : null} */}
//                 </ListGroup.Item>
//               );
//             })()}
 
//  {/* <ListGroup.Item className="py-1 px-2">
//   <GeoAltFill className="me-2" />
//   <span>
//     {incident.locationName || incident.buildingName || '—'}
//     {incident.myLocation ? ` | ${incident.myLocation}` : ''}
//   </span>
// </ListGroup.Item> */}

// <ListGroup.Item className="py-1 px-2">
//   <GeoAltFill className="me-2" />
//   <span>
//     {incident.locationName || incident.buildingName || '—'}
//     {incident.myLocation ? ` | Location: ${incident.myLocation}` : ''}
//     {safeStatusArr.length > 0 ? ` | Status: ${safeStatusArr.join(', ')}` : ''}
//   </span>
// </ListGroup.Item>

//             <ListGroup.Item
//               className="py-1 px-2 d-flex justify-content-between align-items-center"
//               style={{ background: '#eee' }}>
//               {/* LEFT: phone icon + name + status */}
//               <span className="d-flex align-items-center gap-2">
//                 {phoneNumber && (
//                   <a href={`tel:${phoneNumber}`} title={`Call ${phoneNumber}`}>
//                     <TelephoneFill size={16}/>
//                   </a>
//                 )}
//                 <span>
//                   <b>By</b> {incident.createdByName || '—'}
//                   {safeCreatorStatus.length > 0 && (<> – <em>{safeCreatorStatus.join(', ')}</em></>)}
//                 </span>
//               </span>

//               {/* RIGHT: live badge */}
//               <div className="d-flex align-items-center gap-2">
//                 <Badge bg={incident.isActive ? 'success' : 'secondary'} className="px-2">
//                   {incident.isActive ? 'ACTIVE' : 'INACTIVE'}
//                 </Badge>
//               </div>
//             </ListGroup.Item>
//           </ListGroup>

//           {/* chat */}
//           <div className="flex-grow-1 d-flex flex-column border rounded"
//             style={{ minHeight: 0, overflow: 'hidden' }}>
//             <IncidentAlertChat
//               incidentID={incident.incidentID}
//               iAccountID={incident.iAccountID}
//               iCampusID={incident.iCampusID}
//               iBuildingID={incident.iBuildingID}
//               buildingName={incident.locationName || incident.buildingName || '—'}
//               myLocation={incident.myLocation}
//               myStatus={safeStatusArr}
//             />
//           </div>
//         </Card.Body>
//       </Card>

//       {/* Help modal */}
//       <Modal show={showHelp} onHide={() => setShowHelp(false)} centered scrollable>
//         <Modal.Header closeButton>
//           <Modal.Title>How to use the Active Incident page</Modal.Title>
//         </Modal.Header>
//         <Modal.Body>
//           <ol className="mb-0 ps-3">
//             <li><b>Review the banner</b> for incident type, category, and <b>Mode</b> (DRILL or REAL).</li>
//             <li><b>Update My Status</b> to share your current condition; this also posts a message to the incident thread (geo is included when available).</li>
//             <li><b>Update Room Location</b> to add your room/area (admins can use the gear to edit the location list).</li>
//             <li>Use the <b>chat</b> area below to read/post messages tied to this incident.</li>
//             <li>If needed, tap the <b>phone icon</b> to call the incident creator’s number.</li>
//             <li>The footer row shows who created the incident and their latest status; the badge indicates if the incident is <b>Active</b> or <b>Inactive</b>.</li>
//             {isAdmin && <li><b>Admins:</b> use the gear to open the Location Editor; toggle Open/Close incident is available in admin tools (where enabled).</li>}
//           </ol>
//         </Modal.Body>
//         <Modal.Footer>
//           <Button variant="primary" onClick={() => setShowHelp(false)}>Close</Button>
//         </Modal.Footer>
//       </Modal>

//       {/* modals */}

// <Modal show={showAdminPost} onHide={() => setShowAdminPost(false)} centered>
//   <Modal.Header closeButton>
//     <Modal.Title>Admin Message</Modal.Title>
//   </Modal.Header>
//   <Modal.Body style={{ fontSize: 18, fontWeight: 700 }}>
//     {String(incident?.adminPostMessage || '').trim() || '—'}
//   </Modal.Body>
//   <Modal.Footer>
//     <Button variant="primary" onClick={() => setShowAdminPost(false)}>Close</Button>
//   </Modal.Footer>
// </Modal>



//             <ActionGuideModal
//         show={showActionGuide}
//         onHide={() => setShowActionGuide(false)}
//         loading={guideLoading}
//         titleText={`${incident.srpLabel || incidentTypeName || incident.incidentType || '—'} — ${incident.incidentCategory || incidentCategory || ''}`}
//         guideText={guideText}
//       />

//  <ReportStatusModal
//   show={showStatusModal}
//   onHide={() => setShowStatusModal(false)}
//   onSave={updateMyStatus}
//   initial={statusArr}
// />

//       <IncidentTypeModal
//         show   ={showIncidentModal}
//         onHide ={()=>setShowIncidentModal(false)}
//         onSave  ={({ incidentType, category }) => {
//           setIncidentTypeName(incidentType);
//           setIncidentCategory(category);
//         }}
//       />
//       <UpdateLocationModal
//         show={showLocModal}
//         onHide={() => setShowLocModal(false)}
//         incident={incident}
//         userContext={{
//           user,
//           iAccountID : user?.iAccountID,
//           iBuildingID: iBuildingID,
//           iCampusID  : incident?.iCampusID,
//         }}
//         onSaved={(loc) => {
//   const next = String(loc || '');
//   setIncident(prev => (prev ? { ...prev, myLocation: next } : prev));

//   // persist (survives refresh)
//   try {
//     if (locStorageKey) sessionStorage.setItem(locStorageKey, next);
//   } catch {
//     // ignore storage errors
//   }
// }}
//       />
//            <LocationEditorModal
//         show={showLocEditor}
//         onHide={() => setShowLocEditor(false)}
//         incident={incident}
//         userContext={{
//           user,
//           iAccountID : user?.iAccountID,
//           iBuildingID: iBuildingID,
//           iCampusID: incident?.iCampusID,
//         }}
//         toggleActive={toggleActive}
//         onIncidentChange={(row) => {
//           setIncident(prev => ({ ...(prev || {}), ...(row || {}) }));
//         }}
//         onActionGuideVisibilityChange={(nextValue) => {
//           const next = !!nextValue;
//           setShowActionGuideButton(next);
//           if (!next) setShowActionGuide(false);
//         }}
//       />



//     </Container>
//   );
// }




 
 